import React from 'react';
import styled from 'styled-components';
import { FaUsers, FaFileExport, FaFileImport } from 'react-icons/fa';
import { designTokens } from '../foundation/DesignTokens';
import { Drawer, DrawerList, DrawerItem } from './Drawer';

// Styled sidebar title
const SidebarTitle = styled.div`
  font-family: ${designTokens.fonts.heading};
  font-weight: 800;
  font-size: 1.25rem;
  color: ${designTokens.colors.gray[900]};
  margin-bottom: ${designTokens.spacing.xl};
  padding-left: 12px;
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.sm};

  @media (max-width: 900px) {
    font-size: 1.1rem;
    margin-bottom: ${designTokens.spacing.lg};
  }
`;

// Section title for subsections
const SectionTitle = styled.div`
  font-family: ${designTokens.fonts.heading};
  font-weight: 700;
  font-size: 0.85rem;
  color: ${designTokens.colors.gray[500]};
  margin-bottom: ${designTokens.spacing.sm};
  padding-left: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

// Enhanced drawer item with better styling
const NavItem = styled(DrawerItem)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${designTokens.radius.md};
  color: ${designTokens.colors.gray[700]};
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  margin-bottom: 4px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  width: 100%;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: ${designTokens.colors.gray[50]};
    color: ${designTokens.colors.gray[900]};
  }

  ${(props) =>
    props.active &&
    `
    background-color: ${designTokens.colors.primary[50]};
    color: ${designTokens.colors.primary[600]};
    font-weight: 600;
    border-color: ${designTokens.colors.primary[100]};
  `}

  ${(props) =>
    props.disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed;
  `}

  @media (max-width: 900px) {
    font-size: 0.87rem;
    padding: 10px 14px;
    gap: 10px;
  }
`;

// Icon wrapper
const NavIcon = styled.span`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.active ? 1 : 0.7)};
  transition: opacity 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 900px) {
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

// Divider for separating sections
const NavDivider = styled.div`
  height: 1px;
  background-color: ${designTokens.colors.gray[200]};
  margin: ${designTokens.spacing.lg} 0;
`;

// Navigation items configuration for Contacts
const navigationItems = [
  {
    id: 'all',
    label: 'Todos los contactos',
    Icon: FaUsers,
  },
];

// Action items
const actionItems = [
  {
    id: 'export',
    label: 'Exportar',
    Icon: FaFileExport,
  },
  {
    id: 'import',
    label: 'Importar',
    Icon: FaFileImport,
  },
];

// `hiddenActions`: ids of `actionItems` the drawer must not offer (e.g. a
// sede tab hides 'import').
const ContactsNavigation = ({
  activeView = 'all',
  onViewChange,
  onActionClick,
  hiddenActions = [],
}) => {
  const visibleActionItems = actionItems.filter(
    (item) => !hiddenActions.includes(item.id)
  );

  const handleNavClick = (item) => {
    if (item.disabled) return;

    if (onViewChange) {
      onViewChange(item.id);
    }
  };

  const handleActionClick = (item) => {
    if (onActionClick) {
      onActionClick(item.id);
    }
  };

  return (
    <Drawer>
      <SidebarTitle>Base de Datos</SidebarTitle>

      <DrawerList>
        {navigationItems.map((item) => (
          <NavItem
            key={item.id}
            active={activeView === item.id}
            disabled={item.disabled}
            onClick={() => handleNavClick(item)}>
            <NavIcon active={activeView === item.id}><item.Icon /></NavIcon>
            {item.label}
          </NavItem>
        ))}

        <NavDivider />

        <SectionTitle>Acciones</SectionTitle>
        {visibleActionItems.map((item) => (
          <NavItem
            key={item.id}
            onClick={() => handleActionClick(item)}>
            <NavIcon><item.Icon /></NavIcon>
            {item.label}
          </NavItem>
        ))}
      </DrawerList>
    </Drawer>
  );
};

export default ContactsNavigation;
